import AppLayout from "@cloudscape-design/components/app-layout";
import BreadcrumbGroup, { BreadcrumbGroupProps } from "@cloudscape-design/components/breadcrumb-group";
import Flashbar from "@cloudscape-design/components/flashbar";
import * as React from "react";
import { useLocation } from "react-router-dom";
import {
  DirectRenderingContainersProvider,
  useDebugPanel,
  useDirectRenderingStore,
  useLayoutManager,
  useNavigation,
} from "@amzn/awsmp-max-agentic-renderer";
import styles from "./Layout.module.css";
import { ScrollToTop } from "@amzn/awsmp-max-ux-patterns/ScrollToTop";
import { DebugPanelContainer } from "@amzn/awsmp-max-ux-patterns/DebugPanel";
import { BreadcrumbItem } from "@amzn/awsmp-max-agentic-renderer-interface";
import { BrowserCompatibilityWarning } from "./browser-compatibility";

import { HelpPanelContent } from "@amzn/aws-widget-react-wrappers";

export interface DirectRenderingLayoutProps {
  children: React.ReactNode;
  dataAttributes?: Record<string, string>;
  /** If true, starts with no breadcrumbs (neither static nor store). Defaults to false. */
  startWithNoBreadcrumbs?: boolean;
}

/**
 * Component-based Layout component
 *
 * Provides layout areas for different types of content:
 * - Split Panel: Renders custom React content via direct rendering system
 * - Help Panel: Renders AWS help widgets based on pathslug registration
 * - Modal: Uses Cloudscape Modal components directly (no wrapper needed)
 * - Flashbar: Displays notifications via layout manager
 * - Breadcrumbs: Displays navigation breadcrumbs via navigation system
 *
 * To render layout items, use the following wrapper components:
 *
 * @example Split Panel
 * ```tsx
 * <LayoutSplitPanel open={isOpen} header="My Panel" onToggle={setIsOpen}>
 *   <MyCustomContent />
 * </LayoutSplitPanel>
 * ```
 *
 * @example Help Panel
 * ```tsx
 * <LayoutHelpPanel open={showHelp} pathslug="help/getting-started" onToggle={setShowHelp} />
 * ```
 *
 * @example Modal
 * ```tsx
 * <Modal visible={showModal} header="My Modal" onDismiss={() => setShowModal(false)}>
 *   <MyModalContent />
 * </Modal>
 * ```
 *
 * @example Flashbar
 * ```tsx
 * const { flashbar } = useLayoutManager();
 *
 * // Add notification
 * flashbar.addItem({
 *   type: "success",
 *   content: "Operation completed successfully",
 *   dismissible: true
 * });
 * ```
 */
export const DirectRenderingLayout: React.FC<DirectRenderingLayoutProps> = ({ children, dataAttributes, startWithNoBreadcrumbs = false }) => {
  const { splitPanel, helpPanel } = useDirectRenderingStore();
  const { flashbar, registerSticky } = useLayoutManager();
  const { useBreadcrumbs, navigateByHref, registry } = useNavigation();
  const storeBreadcrumbs = useBreadcrumbs();
  const location = useLocation();
  const debugPanel = useDebugPanel();

  // Use state + callback ref to track container changes
  const [splitPanelContainer, setSplitPanelContainer] = React.useState<HTMLDivElement | null>(null);

  // Split panel position preference (matches old system pattern)
  const [splitPanelPreference, onSplitPanelPreferencesChange] = React.useState<"side" | "bottom">("side");

  // Create ref for breadcrumbs container
  const breadcrumbsRef = React.useRef<HTMLDivElement>(null);

  // Generate static breadcrumbs from route definitions when store is empty
  const staticBreadcrumbs: BreadcrumbItem[] = React.useMemo(() => {
    // If startWithNoBreadcrumbs is true, return empty array
    if (startWithNoBreadcrumbs) {
      return [];
    }

    if (storeBreadcrumbs.length > 0) {
      return [];
    }

    const routeChain = registry.getBreadcrumbRoutes(location.pathname);
    return routeChain.map(({ route, params }) => ({
      text: route.getBreadcrumbText(params),
      href: route.link(params),
    }));
  }, [startWithNoBreadcrumbs, storeBreadcrumbs.length, registry, location.pathname]);

  // Use store breadcrumbs if available, otherwise use static breadcrumbs
  // Store breadcrumbs (from setBreadcrumbs()) always take precedence
  // startWithNoBreadcrumbs only affects static breadcrumbs, not store breadcrumbs
  const breadcrumbs = storeBreadcrumbs.length > 0 ? storeBreadcrumbs : startWithNoBreadcrumbs ? [] : staticBreadcrumbs;

  // Register breadcrumbs container as sticky element
  React.useEffect(() => {
    if (breadcrumbsRef.current) {
      return registerSticky(breadcrumbsRef, "breadcrumbs");
    }
  }, [registerSticky]);

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (event: CustomEvent<BreadcrumbGroupProps.ClickDetail<BreadcrumbItem>>) => {
    if (!event.detail.item.isAbsoluteUrl) {
      event.preventDefault();
      navigateByHref(event.detail.href);
    }
  };

  const handleBreadcrumbFollow = (event: CustomEvent<BreadcrumbGroupProps.ClickDetail<BreadcrumbItem>>) => {
    if (!event.detail.item.isAbsoluteUrl) {
      event.preventDefault();
      navigateByHref(event.detail.href);
    }
  };

  // Stable handlers with useCallback
  const handleSplitPanelToggle = React.useCallback(
    ({ detail }: { detail: { open: boolean } }) => {
      if (splitPanel?.onToggle) {
        splitPanel.onToggle(detail.open);
      }
    },
    [splitPanel?.onToggle],
  );

  const handleSplitPanelResize = React.useCallback(
    ({ detail }: { detail: { size: number } }) => {
      if (splitPanel?.onResize) {
        splitPanel.onResize(detail.size);
      }
    },
    [splitPanel?.onResize],
  );

  const handleToolsToggle = React.useCallback(
    ({ detail }: { detail: { open: boolean } }) => {
      if (helpPanel?.onToggle) {
        helpPanel.onToggle(detail.open);
      }
    },
    [helpPanel?.onToggle],
  );

  return (
    <>
      <ScrollToTop />
      <DirectRenderingContainersProvider splitPanelContainer={splitPanelContainer}>
        <div className={styles.layout}>
          <AppLayout
            {...dataAttributes}
            navigationHide={true}
            breadcrumbs={
              <div id={"breadcrumb-ref"} ref={breadcrumbsRef}>
                <BreadcrumbGroup items={breadcrumbs} onClick={handleBreadcrumbClick} onFollow={handleBreadcrumbFollow} />
              </div>
            }
            content={
              <>
                <BrowserCompatibilityWarning />
                {children}
              </>
            }
            splitPanelOpen={splitPanel?.open ?? false}
            splitPanelSize={splitPanel?.size ?? 900}
            splitPanelPreferences={{
              position: splitPanelPreference,
            }}
            splitPanel={<div ref={setSplitPanelContainer} />}
            onSplitPanelToggle={handleSplitPanelToggle}
            onSplitPanelResize={handleSplitPanelResize}
            onSplitPanelPreferencesChange={({ detail }) => onSplitPanelPreferencesChange(detail.position)}
            notifications={
              <Flashbar
                stackItems={true}
                items={flashbar.items.map((item) => ({
                  ...item,
                  onDismiss: item.dismissible !== false ? () => flashbar.removeItem(item.id) : undefined,
                }))}
                i18nStrings={{
                  ariaLabel: "Notifications",
                  errorIconAriaLabel: "Error",
                  infoIconAriaLabel: "Information",
                  inProgressIconAriaLabel: "In progress",
                  successIconAriaLabel: "Success",
                  warningIconAriaLabel: "Warning",
                }}
              />
            }
            toolsHide={!helpPanel}
            tools={helpPanel ? <HelpPanelContent key={helpPanel.pathslug} pathslug={helpPanel.pathslug} /> : undefined}
            toolsOpen={helpPanel?.open ?? false}
            onToolsChange={handleToolsToggle}
          />
        </div>
      </DirectRenderingContainersProvider>
      <DebugPanelContainer {...debugPanel} />
    </>
  );
};
